import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, Calendar as CalendarIcon, CheckCircle, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomResult = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomRequest();
  }, [id]);

  const fetchCustomRequest = async () => {
    try {
      const response = await axios.get(`${API}/custom-project-request/${id}`);
      setRequest(response.data);
    } catch (error) {
      toast.error('Demande non trouvée');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#335C2E] mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600">Demande non trouvée</p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-6 py-2 bg-[#335C2E] text-white rounded hover:bg-[#2a4a25]"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-6">
          <div className="flex items-center justify-center mb-6">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-center mb-2" style={{ color: '#335C2E' }}>
            Demande de Projet Personnalisé
          </h1>
          <p className="text-center text-gray-600 text-lg">
            Votre demande a été transmise avec succès!
          </p>
        </div>

        {/* Important Message */}
        <div className="bg-[#C7A027] bg-opacity-10 border-l-4 border-[#C7A027] p-6 rounded-r-lg mb-6">
          <h2 className="text-xl font-semibold mb-2" style={{ color: '#335C2E' }}>
            Projet nécessitant une évaluation personnalisée
          </h2>
          <p className="text-gray-700 mb-4">
            Les dimensions de votre projet nécessitent une évaluation sur mesure. Un membre de notre équipe vous contactera sous peu pour :
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
            <li>Discuter des détails de votre projet</li>
            <li>Évaluer vos besoins spécifiques</li>
            <li>Vous fournir une estimation précise et détaillée</li>
            <li>Planifier un rendez-vous si nécessaire</li>
          </ul>
        </div>

        {/* Project Details */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-6">
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#335C2E' }}>
            Détails de votre projet
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Customer Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg" style={{ color: '#335C2E' }}>
                Vos coordonnées
              </h3>
              <div>
                <p className="text-sm text-gray-600">Nom</p>
                <p className="font-medium">{request.customer.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{request.customer.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Téléphone</p>
                <p className="font-medium">{request.customer.phone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Adresse</p>
                <p className="font-medium">{request.customer.address}</p>
              </div>
            </div>

            {/* Project Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg" style={{ color: '#335C2E' }}>
                Informations du projet
              </h3>
              <div>
                <p className="text-sm text-gray-600">Type de projet</p>
                <p className="font-medium">{request.project.project_type}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Dimensions</p>
                <p className="font-medium">
                  {request.project.width} pi × {request.project.length} pi
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Surface totale</p>
                <p className="font-medium">{request.project.surface} pi²</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date de démarrage souhaitée</p>
                <p className="font-medium">{request.project.start_date}</p>
              </div>
              {request.project.options && request.project.options.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600">Options demandées</p>
                  <ul className="list-disc list-inside">
                    {request.project.options.map((opt, idx) => (
                      <li key={idx} className="font-medium">{opt}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#335C2E' }}>
            Besoin de nous joindre ?
          </h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5" style={{ color: '#C7A027' }} />
              <div>
                <p className="text-sm text-gray-600">Téléphone</p>
                <p className="font-medium">514 213 1783</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5" style={{ color: '#C7A027' }} />
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">info@refacingvr.com</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="text-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-8 py-3 text-white rounded-lg transition-colors"
            style={{ backgroundColor: '#335C2E' }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2a4a25'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#335C2E'}
          >
            <Home size={20} />
            Retour à l'accueil
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomResult;
